import { getRequest, formDataPostRequest, postRequest } from 'config/Axios/baseConfig';
import {
  CategoriesResponse,
  CreateTicketPayload,
  CreateTicketResponse,
  MyTicketsResponse,
  TicketDetailResponse,
  SyncStatusResponse
} from 'Interfaces/SupportTickets/supportTicketInterfaces';

/**
 * Support Ticket Services
 * All API calls for support ticket functionality
 */
export const supportTicketServices = {
  /**
   * Fetch all ticket categories
   */
  getCategories: (): Promise<CategoriesResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<CategoriesResponse>('support-tickets/categories');
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  },

  /**
   * Create a JSM ticket
   * @param payload - Ticket creation payload
   */
  createJSMTicket: (payload: CreateTicketPayload): Promise<CreateTicketResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        // Build FormData for file uploads
        const formData = new FormData();
        
        // Required fields with correct names for backend
        formData.append('category_id', payload.category);
        formData.append('title', payload.title);
        formData.append('description', payload.description);

        // Page route = pathname only; page_url = full URL (required by backend)
        const fullUrl = payload.browserInfo?.url ?? payload.additionalContext?.currentUrl ?? '';
        if (fullUrl) {
          try {
            formData.append('page_route', new URL(fullUrl).pathname);
          } catch {
            formData.append('page_route', fullUrl);
          }
          formData.append('page_url', fullUrl);
        }
        if (payload.browserInfo) {
          formData.append('browser_info', JSON.stringify(payload.browserInfo));
        }
        if (payload.browserInfo?.screenResolution) {
          formData.append('screen_resolution', payload.browserInfo.screenResolution);
        }

        // Environment as top-level field
        const environment =
          payload.additionalContext?.environment ||
          import.meta.env.MODE ||
          'development';
        formData.append('environment', environment);

        if (payload.additionalContext) {
          formData.append('additional_context', JSON.stringify(payload.additionalContext));
        }
        const userRole = payload.additionalContext?.userRole;
        if (userRole !== undefined && userRole !== null) {
          formData.append('user_role', String(userRole));
        }

        if (payload.screenshot) {
          formData.append('screenshot', payload.screenshot);
        }

        if (payload.attachments && payload.attachments.length > 0) {
          payload.attachments.forEach((file) => {
            formData.append('attachments', file);
          });
        }

        const { data } = await formDataPostRequest<CreateTicketResponse>(
          'support-tickets/jsm/create',
          formData
        );
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  },

  /**
   * Fetch user's tickets
   * @param searchQuery - Optional search query
   */
  getMyTickets: (searchQuery?: string): Promise<MyTicketsResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const url = searchQuery
          ? `support-tickets/jsm/my-tickets?search=${encodeURIComponent(searchQuery)}`
          : 'support-tickets/jsm/my-tickets';

        const { data } = await getRequest<MyTicketsResponse>(url);
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  },

  /**
   * Fetch ticket detail by ID
   * @param ticketId - Jira ticket ID
   */
  getTicketDetail: (ticketId: string): Promise<TicketDetailResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<TicketDetailResponse>(
          `support-tickets/jsm/ticket/${ticketId}`
        );
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  },

  /**
   * Sync ticket status from Jira
   * @param ticketId - Jira ticket ID
   */
  syncTicketStatus: (ticketId: string): Promise<SyncStatusResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<SyncStatusResponse>(
          `support-tickets/ticket/${ticketId}/sync-status`,
          {}
        );
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  }
};
